/* import __COLOCATED_TEMPLATE__ from './drill-in-preview-panel.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from 'tracked-built-ins';
import { action } from '@ember/object';
import Conversation from 'embercom/objects/inbox/conversation';
import { request } from 'embercom/lib/inbox/requests';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import type InboxApi from 'embercom/services/inbox-api';
import type ApplicationInstance from '@ember/application/instance';
import { registerDestructor } from '@ember/destroyable';
import type Router from '@ember/routing/router-service';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { isPresent } from '@ember/utils';
import type IntlService from 'ember-intl/services/intl';
import type Session from 'embercom/services/session';
import {
  type CopilotPromptResponsePairWireFormat,
  CopilotPromptResponsePair,
} from 'embercom/lib/inbox2/copilot/types';
import { captureException } from 'embercom/lib/sentry';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import storage from 'embercom/vendor/intercom/storage';
import { CX_REASON_TO_COLOR } from 'embercom/components/reporting/automation/ai-insights/conversation-stream';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';

const CONVERSATION_EVENTS_HIDDEN_STORAGE_KEY = 'conversation_events_hidden';

interface Args {
  onClose: () => void;
  eventPlace: string;
  isStandalone?: boolean;
  hideConversationEventsMenu?: boolean;
}

interface Signature {
  Args: Args;
  Blocks: {
    default: [StreamResourceManager];
  };
}

type PanelResource = Conversation | CopilotPromptResponsePair;
type PanelResourceType = typeof Conversation | typeof CopilotPromptResponsePair;

export interface StreamResourceManager {
  showPreviewSideBar: (
    resourceId: number,
    resourceType: PanelResourceType,
    relatedResourceIds: number[],
  ) => TaskGenerator<void>;
  showConversationSideBarAndHighlightPart: (
    selectedConversationId: number,
    selectedPartId: number,
    conversationIds: number[],
  ) => TaskGenerator<void>;
}

type GroupList = [
  {
    items: GroupListItem[];
  },
];

type GroupListItem = {
  text: string;
  icon: string;
  onSelectItem: () => void;
  isSelected: boolean;
  component: string;
};

export default class DrillInPreviewPanel extends Component<Signature> {
  @service declare session: Session;
  @service declare inboxApi: InboxApi;
  @service declare router: Router;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;

  @tracked activeResource?: PanelResource = undefined;
  @tracked activeResourceType?: PanelResourceType = undefined;
  @tracked activeResourceId?: number = undefined;
  @tracked metadata?: any = undefined;
  @tracked relatedResourceIds: number[] = [];
  @trackedInLocalStorage({
    keyName: CONVERSATION_EVENTS_HIDDEN_STORAGE_KEY,
  })
  hideConversationEvents = storage.get(CONVERSATION_EVENTS_HIDDEN_STORAGE_KEY) ?? false;
  @tracked contentFetchError = false;
  private cachedResources: Record<string, PanelResource> = {};

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);
    registerDestructor(this, () => {
      this.cachedResources = {};
    });
    if (this.args.hideConversationEventsMenu) {
      this.hideConversationEvents = true;
    }
  }

  get groupList(): GroupList {
    return [
      {
        items: [
          {
            text: this.intl.t('inbox.conversation-header.conversation-events'),
            icon: 'eye',
            onSelectItem: this.toggleHideConversationEvents,
            isSelected: !this.hideConversationEvents,
            component:
              'reporting/custom/report/conversation-stream/conversation-stream-options-dropdown-item',
          },
        ],
      },
    ];
  }

  get conversation(): Conversation | undefined {
    if (!(this.activeResource instanceof Conversation)) {
      return;
    }

    return this.activeResource;
  }

  get copilotPromptResponsePair(): CopilotPromptResponsePair | undefined {
    if (!(this.activeResource instanceof CopilotPromptResponsePair)) {
      return;
    }

    return this.activeResource;
  }

  private getFromCache(
    resourceId: number,
    resourceType: PanelResourceType,
  ): PanelResource | undefined {
    return this.cachedResources[`${resourceType.name}/${resourceId}`];
  }

  private addToCache(resourceId: number, resource: PanelResource | undefined) {
    if (resource) {
      this.cachedResources[`${resource.constructor.name}/${resourceId}`] = resource;
    }
  }

  get app() {
    return this.appService.app;
  }

  get currentStreamPage() {
    return this.currentResourceIndex + 1;
  }

  get sideDrawerTitle() {
    if (this.activeResourceType === Conversation) {
      if (this.conversation) {
        if (this.conversation.ticketCategory === 'tracker') {
          return this.intl.t(
            'components.reporting.custom.report.conversation-stream.tracker-ticket',
          );
        } else {
          return this.activeResource !== undefined
            ? this.conversation.firstParticipant.displayAs
            : '';
        }
      } else {
        return this.intl.t(
          'components.reporting.custom.report.conversation-stream.sidedrawer-title',
        );
      }
    } else {
      return this.intl.t(
        'components.reporting.custom.report.assistant-conversation-stream.sidedrawer-title',
      );
    }
  }

  get summary() {
    return this.intl.t('reporting.conversation-stream.summary', {
      currentStreamPage: this.intl.formatNumber(this.currentStreamPage),
      totalStreams: this.intl.formatNumber(this.relatedResourceIds.length),
    });
  }

  get showPreviewPanel() {
    return (
      taskFor(this.changeActiveResource).isRunning ||
      isPresent(this.activeResource) ||
      this.contentFetchError
    );
  }

  get isNotLoadingResource() {
    return !taskFor(this.changeActiveResource).isRunning;
  }

  @restartableTask
  private *changeActiveResource(resourceId: number) {
    let cachedResource = this.getFromCache(resourceId, this.activeResourceType!);
    if (cachedResource) {
      this.activeResource = cachedResource;
      this.activeResourceId = resourceId;
      return;
    }

    yield taskFor(this.fetchAndActivateResource).perform(resourceId);
  }

  @restartableTask
  private *fetchAndActivateResource(resourceId: number) {
    let resource: PanelResource | undefined;
    if (this.activeResourceType === Conversation) {
      try {
        resource = (yield this.inboxApi.fetchConversation(resourceId)) as Conversation;
      } catch (error) {
        this.contentFetchError = true;
      }
    } else {
      let response = (yield request(
        `/ember/inbox/copilot_conversations/prompt_response_pair?prompt_part_id=${resourceId}&app_id=${this.session.workspace.id}`,
      )) as Response;
      let json = (yield response.json()) as CopilotPromptResponsePairWireFormat;
      resource = CopilotPromptResponsePair.deserialize(json);
    }

    this.addToCache(resourceId, resource);
    this.activeResource = resource;
    this.activeResourceId = resourceId;
  }

  @restartableTask
  *showPreviewSideBar(
    resourceId: number,
    resourceType: PanelResourceType,
    relatedResourceIds: number[],
  ): TaskGenerator<void> {
    this.activeResourceType = resourceType;
    this.relatedResourceIds = relatedResourceIds.uniq();

    yield taskFor(this.changeActiveResource).perform(resourceId);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'open',
      object: 'data_table_conversation_stream_link',
      place: this.args.eventPlace,
      resource_type: this.resourceTypeName(resourceType),
    });
  }

  @restartableTask
  *showConversationSideBarAndHighlightPart(
    selectedConversationId: number,
    selectedPartId: number,
    conversationIds: number[],
  ) {
    window.location.hash = `part_id=comment-${selectedConversationId}-${selectedPartId}`;
    // we need to update hash before tracked property
    this.metadata = { partId: selectedPartId };
    yield taskFor(this.showPreviewSideBar).perform(
      selectedConversationId,
      Conversation,
      conversationIds,
    );
  }

  private resourceTypeName(resourceType: PanelResourceType): string {
    switch (resourceType) {
      case Conversation:
        return 'conversation';
      case CopilotPromptResponsePair:
        return 'copilot_prompt_response_pair';
      default:
        captureException(new Error('Unknown resource type'), {
          fingerprint: ['copilot-reporting', 'unknown-resource-type'],
          extra: { resourceType },
        });
        return 'unknown';
    }
  }

  private removeLocationHash() {
    let noHashURL = window.location.href.replace(/#.*$/, '');
    window.history.replaceState('', '', noHashURL);
  }

  @action
  onClose() {
    this.activeResource = undefined;
    this.activeResourceType = undefined;
    this.activeResourceId = undefined;
    this.metadata = undefined;
    this.contentFetchError = false;
    this.removeLocationHash();
    this.args.onClose();
  }

  @action
  toggleHideConversationEvents() {
    this.hideConversationEvents = !this.hideConversationEvents;
  }

  get currentResourceIndex() {
    if (this.activeResourceId) {
      return this.relatedResourceIds.indexOf(this.activeResourceId);
    }
    return 0;
  }

  get hasPrev() {
    return this.currentResourceIndex > 0;
  }

  get hasNext() {
    return this.currentResourceIndex < this.relatedResourceIds.length - 1;
  }

  @restartableTask
  *onNext() {
    if (this.hasNext) {
      this.contentFetchError = false;
      let nextId = this.relatedResourceIds[this.currentResourceIndex + 1];
      yield taskFor(this.changeActiveResource).perform(nextId);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'data_table_conversation_stream_navigation',
        place: this.args.eventPlace,
      });
    }
  }

  @restartableTask
  *onPrev() {
    if (this.hasPrev) {
      this.contentFetchError = false;
      let nextId = this.relatedResourceIds[this.currentResourceIndex - 1];
      yield taskFor(this.changeActiveResource).perform(nextId);
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'data_table_conversation_stream_navigation',
        place: this.args.eventPlace,
      });
    }
  }

  get cxSummary(): string | undefined {
    let cxSummaryField = this.appService.app.useNewCxSummary
      ? 'customer_experience_summary'
      : 'customer_experience_explanation';

    return this.conversation?.attributesById?.[cxSummaryField]?.value as string | undefined;
  }

  get allCxAttributes(): { label: string; color: string }[] {
    if (!this.conversation) {
      return [];
    }
    let attributes = this.conversation.attributesById?.['customer_experience_attributes']
      ?.value as string;
    if (!attributes) {
      return [];
    }
    let availableValues = this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );
    return attributes.split('\n').map((attribute) => {
      let colorConfig = CX_REASON_TO_COLOR[attribute as keyof typeof CX_REASON_TO_COLOR];
      return {
        label: availableValues[attribute] as string,
        color: colorConfig?.color || '', // Fallback to empty string if not found
      };
    });
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::DrillInPreviewPanel': typeof DrillInPreviewPanel;
    'reporting/custom/report/drill-in-preview-panel': typeof DrillInPreviewPanel;
  }
}
